import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Badge,
  Row,
  Table,
  Tooltip,
  UncontrolledTooltip,
  Popover,
  PopoverHeader,
  PopoverBody,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import {
  getPlacedOrders,
  updateDeliveryDate,
  updateShipmentDate,
  addNewShipment,
  deleteShipment,
  getShipmentInvoiceSummary,
  uploadShipmentInvoice,
  downloadShipmentInvoice,
  updateShipmentDescription,
} from "../service";
import { updateOrderStatus } from "../../../project/service";
import { Notification } from "../../../common/style";
import "../styles/orders.css";

const Orders = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [placedFilter, setPlacedFilter] = useState([]);
  const [projectTypeFilter, setProjectTypeFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [orders, setOrders] = useState([]);
  const [mainTab, setMainTab] = useState("orders");
  const [activeTab, setActiveTab] = useState("pending");
  const [shipmentTab, setShipmentTab] = useState("pending");
  const [shipmentSortKey, setShipmentSortKey] = useState("date");
  const [shipmentSortAsc, setShipmentSortAsc] = useState(true);
  const [tooltipOpenId, setTooltipOpenId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false); // NEW: modal state for delivery date update
  const [isLoading, setIsLoading] = useState(false);
  const [addShipmentModalOpen, setAddShipmentModalOpen] = useState(false);
  const [newShipmentPlannedDate, setNewShipmentPlannedDate] = useState("");
  const [currentOrderForShipment, setCurrentOrderForShipment] = useState(null);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({});
  const [invoiceSummaryModalOpen, setInvoiceSummaryModalOpen] = useState(false);
  const [invoiceSummaryData, setInvoiceSummaryData] = useState(null);
  const [isLoadingInvoiceSummary, setIsLoadingInvoiceSummary] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dateChangeModalOpen, setDateChangeModalOpen] = useState(false);
  const [editingShipmentForDateChange, setEditingShipmentForDateChange] = useState(null);
  const [revisedDate, setRevisedDate] = useState("");
  const [dateChangeReason, setDateChangeReason] = useState("");
  const [statusUpdateModalOpen, setStatusUpdateModalOpen] = useState(false);
  const [editingShipmentForStatusUpdate, setEditingShipmentForStatusUpdate] = useState(null);
  const [statusUpdateFile, setStatusUpdateFile] = useState(null);
  const [statusUpdatePanelPackagesFile, setStatusUpdatePanelPackagesFile] = useState(null);
  const [statusUpdateHardwarePackagesFile, setStatusUpdateHardwarePackagesFile] = useState(null);
  const [statusUpdateInvoiceFile, setStatusUpdateInvoiceFile] = useState(null);
  const [statusUpdateVehicleNumber, setStatusUpdateVehicleNumber] = useState("");
  const [statusUpdateDriverNumber, setStatusUpdateDriverNumber] = useState("");
  const [statusUpdateOtp, setStatusUpdateOtp] = useState("");
  const [statusUpdatePaymentValue, setStatusUpdatePaymentValue] = useState("");
  const [statusUpdatePaymentDate, setStatusUpdatePaymentDate] = useState("");
  const [statusUpdateOtherReason, setStatusUpdateOtherReason] = useState(false);
  const [statusUpdateApproveReason, setStatusUpdateApproveReason] = useState("");
  const [popoverOpen, setPopoverOpen] = useState({});
  const [orderInfoModalOpen, setOrderInfoModalOpen] = useState(false);
  const [selectedOrderForInfo, setSelectedOrderForInfo] = useState(null);
  const [deleteShipmentModalOpen, setDeleteShipmentModalOpen] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState(null);
  const [replacementShipmentId, setReplacementShipmentId] = useState("");
  const [editDescriptionModalOpen, setEditDescriptionModalOpen] = useState(false);
  const [editingShipmentForDescription, setEditingShipmentForDescription] = useState(null);
  const [editDescriptionValue, setEditDescriptionValue] = useState("");

  useEffect(() => {
    fetchOrders();
  }, []);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const fetchOrders = () => {
    getPlacedOrders()
      .then((res) => {
        setOrders(res);
      })
      .catch((err) => {
        setIsNotificationVisible(true);
        setNotificationMessage({
          type: "danger",
          text: "Error fetching orders: " + (err.message || err.error || err || "Unknown error")
        });
      });
  };

  const STATUS_OPTIONS = [
    { label: "Approved", value: "approved" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Placed", value: "placed" },
    { label: "Production Planning", value: "production_planning" },
    { label: "Production", value: "production" },
    { label: "Partially Delivered", value: "partially_delivered" },
    { label: "Delivered", value: "delivered" },
  ];

  const formatCurrency = (value) =>
    `₹${new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(value)}`;

  const formatDate = (raw) => {
    if (!raw) return "";
    return new Date(raw).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString("en-IN", {
      month: "short",
      year: "2-digit",
    });
  };

  const handleAddShipmentClick = (order) => {
    setCurrentOrderForShipment(order);
    setNewShipmentPlannedDate("");
    setAddShipmentModalOpen(true);
  };

  const handleAddShipment = async () => {
    if (!newShipmentPlannedDate) {
      setIsNotificationVisible(true);
      setNotificationMessage({ type: "warning", text: "Please select a planned date" });
      return;
    }

    try {
      setIsLoading(true);
      await addNewShipment({
        orderId: currentOrderForShipment.id,
        plannedDate: newShipmentPlannedDate,
      });
      fetchOrders();
      setAddShipmentModalOpen(false);
      setIsNotificationVisible(true);
      setNotificationMessage({ type: "success", text: "Shipment added successfully" });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to add new shipment: " + (error.message || error.error || error || "Unknown error")
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteShipmentClick = (shipment) => {
    setShipmentToDelete(shipment);
    setReplacementShipmentId("");
    setDeleteShipmentModalOpen(true);
  };

  const handleDeleteShipment = async () => {
    if (!shipmentToDelete) return;

    try {
      setIsLoading(true);
      await deleteShipment({
        orderId: shipmentToDelete.order.id,
        shipmentId: shipmentToDelete.id,
        replacementShipmentId: replacementShipmentId || null,
      });
      fetchOrders();
      setDeleteShipmentModalOpen(false);
      setShipmentToDelete(null);
      setReplacementShipmentId("");
      setIsNotificationVisible(true);
      setNotificationMessage({ type: "success", text: "Shipment deleted successfully" });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to delete shipment: " + (error.message || error.error || error || "Unknown error")
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditDescriptionClick = (shipment, order) => {
    setEditingShipmentForDescription({ shipment, order });
    setEditDescriptionValue(shipment.description || "");
    setEditDescriptionModalOpen(true);
  };

  const handleSaveDescription = async () => {
    if (!editingShipmentForDescription) return;
    const { shipment, order } = editingShipmentForDescription;
    try {
      setIsLoading(true);
      await updateShipmentDescription({
        orderId: order.id,
        shipmentId: shipment.id,
        description: editDescriptionValue.trim(),
      });
      fetchOrders();
      setEditDescriptionModalOpen(false);
      setEditingShipmentForDescription(null);
      setEditDescriptionValue("");
      setIsNotificationVisible(true);
      setNotificationMessage({ type: "success", text: "Shipment description updated successfully" });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to update shipment description: " + (error.message || error.error || error || "Unknown error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveDateChange = async () => {
    if (!revisedDate) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "warning",
        text: "Please select a revised date"
      });
      return;
    }

    if (!dateChangeReason || dateChangeReason.trim() === "") {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "warning",
        text: "Please provide a reason for the date change"
      });
      return;
    }

    if (!editingShipmentForDateChange) return;

    try {
      setIsLoading(true);
      const response = await updateShipmentDate({
        orderId: editingShipmentForDateChange.order.id,
        shipmentId: editingShipmentForDateChange.id,
        plannedDate: revisedDate,
        reason: dateChangeReason.trim(),
      });

      if (response?.approval_remarks && response?.order) {
        response.order.approval_remarks = response.approval_remarks;
      }

      fetchOrders();
      setDateChangeModalOpen(false);
      setEditingShipmentForDateChange(null);
      setRevisedDate("");
      setDateChangeReason("");
      setIsNotificationVisible(true);
      setNotificationMessage({ type: "success", text: "Shipment date updated successfully" });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to update shipment date: " + (error.message || error.error || error || "Unknown error")
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewInvoiceSummary = async (order, shipment) => {
    try {
      setIsLoadingInvoiceSummary(true);
      setInvoiceSummaryModalOpen(true);
      const response = await getShipmentInvoiceSummary({
        orderId: order.id,
        shipmentId: shipment.id,
      });
      const data = response?.data || response || [];
      setInvoiceSummaryData(Array.isArray(data) ? data : []);
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to load invoice summary: " + (error.message || error.error || error || "Unknown error")
      });
      setInvoiceSummaryModalOpen(false);
    } finally {
      setIsLoadingInvoiceSummary(false);
    }
  };

  const handleDownloadInvoice = async (order, shipment) => {
    try {
      setIsLoading(true);
      const response = await downloadShipmentInvoice({
        orderId: order.id,
        shipmentId: shipment.id,
      });

      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'];

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let filename = 'invoice.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsNotificationVisible(true);
      setNotificationMessage({ type: "success", text: "Invoice downloaded successfully" });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to download invoice: " + (error.message || error.error || error || "Unknown error")
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyTableData = () => {
    if (!invoiceSummaryData || invoiceSummaryData.length === 0) return;

    try {
      let csvContent = "";

      const headers = Object.keys(invoiceSummaryData[0]);
      const formattedHeaders = [
        "S.No",
        ...headers.map(header =>
          header.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
        )
      ];
      csvContent += formattedHeaders.join("\t") + "\n";

      invoiceSummaryData.forEach((row, index) => {
        const values = [
          (index + 1).toString(),
          ...headers.map((header) => {
            const value = row[header];
            if (value === null || value === undefined) return "";
            if (typeof value === 'number') {
              return value % 1 !== 0 ? value.toFixed(3) : value.toString();
            }
            return String(value).replace(/\t/g, " ").replace(/\n/g, " ");
          })
        ];
        csvContent += values.join("\t") + "\n";
      });

      navigator.clipboard.writeText(csvContent).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch((err) => {
        const textArea = document.createElement("textarea");
        textArea.value = csvContent;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text: "Failed to copy data: " + (error.message || "Unknown error")
      });
    }
  };


  // Get parts count for a shipment (directly from shipment data)
  const getShipmentPartsCount = (shipment) => {
    return shipment?.parts_count ?? 0;
  };

  // Get hardwares count for a shipment
  const getShipmentHardwaresCount = (shipment) => {
    return shipment?.hardwares_count ?? 0;
  };

  // Format panels & hardwares display text
  const formatPanelsAndHardwares = (shipment) => {
    const panelsCount = getShipmentPartsCount(shipment);
    const hardwaresCount = getShipmentHardwaresCount(shipment);

    if (panelsCount === 0 && hardwaresCount === 0) {
      return "–";
    }

    const panelsText = `${panelsCount} panel${panelsCount !== 1 ? 's' : ''}`;
    const hardwaresText = hardwaresCount > 0
      ? `${hardwaresCount} hardware${hardwaresCount !== 1 ? 's' : ''}`
      : "-";

    return `${panelsText} | ${hardwaresText}`;
  };

  // Check if shipment has zero parts
  const hasZeroParts = (shipment) => {
    return getShipmentPartsCount(shipment) === 0;
  };

  // Check if shipment is dispatched
  const isDispatched = (shipment) => {
    return shipment.status === "dispatch" || shipment.status === "completed" || shipment.completed_date;
  };

  // Get alternative shipments for replacement (same order, not dispatched/delivered, not the one being deleted)
  const getAlternativeShipments = (order, excludeShipmentId) => {
    if (!order || !order.shipments) return [];
    return order.shipments.filter(s => {
      // Exclude the shipment being deleted
      if (s.id === excludeShipmentId) return false;
      // Exclude dispatched or delivered shipments
      if (isDispatched(s)) return false;
      return true;
    });
  };

  // Get the original planned date from date_revisions
  // date_revisions is in chronological order (oldest to newest):
  // - First element (index 0) = oldest date (original committed date)
  // - Last element = most recent previous date (just replaced by current planned_date)
  const getOriginalPlannedDate = (shipment) => {
    if (shipment.date_revisions && Array.isArray(shipment.date_revisions) && shipment.date_revisions.length > 0) {
      // Get the first entry which represents the original committed date
      return shipment.date_revisions[0].date;
    }
    return shipment.planned_date; // Fallback to planned_date if no revisions
  };

  // Extract shipment number from shipment_name for sorting
  const getShipmentNumber = (shipment) => {
    if (!shipment.shipment_name) return 0;
    // Extract number from shipment_name (e.g., "Shipment #1" or "1" or "#1")
    const match = shipment.shipment_name.match(/#?(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Sort shipments by number
  const sortShipmentsByNumber = (shipments) => {
    return [...shipments].sort((a, b) => {
      const numA = getShipmentNumber(a);
      const numB = getShipmentNumber(b);
      return numA - numB;
    });
  };


  // Get revision count (excluding the original date which is the first entry)
  const getRevisionCount = (shipment) => {
    if (!shipment.date_revisions || !Array.isArray(shipment.date_revisions)) {
      return 0;
    }
    // The first entry is the original date, not a revision
    return Math.max(0, shipment.date_revisions.length - 1);
  };

  // Format revisions for popover display
  const formatRevisions = (shipment) => {
    if (!shipment.date_revisions || !Array.isArray(shipment.date_revisions) || shipment.date_revisions.length === 0) {
      return [];
    }
    return shipment.date_revisions.map((revision, index) => ({
      date: revision.date,
      modifiedAt: revision.modified_at,
      reason: revision.reason,
      modifiedBy: revision.modified_by,
      index: index + 1,
    }));
  };

  const togglePopover = (shipmentId) => {
    setPopoverOpen(prev => ({
      ...prev,
      [shipmentId]: !prev[shipmentId]
    }));
  };

  // Get shipment status display text
  const getShipmentStatusDisplay = (shipment) => {
    if (shipment.completed_date) {
      return "Delivered";
    }

    const statusMap = {
      "draft": "Draft",
      "confirmed": "Confirmed",
      "ready_to_dispatch": "Ready",
      "approve_for_dispatch": "Approved",
      "dispatch": "Dispatched",
      "completed": "Delivered",
    };

    return statusMap[shipment.status] || shipment.status || "";
  };

  // Get next status and button label based on current status
  const getStatusUpdateInfo = (shipment) => {
    if (shipment.completed_date) {
      return null; // No action for delivered shipments
    }

    const statusMap = {
      "draft": { nextStatus: "confirmed", buttonLabel: "Confirm" },
      "confirmed": { nextStatus: "ready_to_dispatch", buttonLabel: "Ready" },
      "ready_to_dispatch": { nextStatus: "approve_for_dispatch", buttonLabel: "Approve" },
      "approve_for_dispatch": { nextStatus: "dispatch", buttonLabel: "Dispatch" },
      "dispatch": { nextStatus: "completed", buttonLabel: "Deliver" },
    };

    return statusMap[shipment.status] || null;
  };

  /**
   * Shipment status actions:
   * - Approve (→ approve_for_dispatch): enabled only when `can_approve_dispatch === true`.
   * - All other steps (Confirm, Ready, Dispatch, Deliver): enabled only when `is_sales_user`.
   * - When both are true: Approve is allowed (finance flag) and other steps are allowed (sales) → full access.
   * - Neither: no actions.
   */
  const canPerformShipmentStatusAction = (shipment, order, statusInfo) => {
    if (!statusInfo) return false;
    const canApproveDispatch = shipment.can_approve_dispatch === true;
    const isSalesUser = !!order?.is_sales_user;

    if (statusInfo.nextStatus === "approve_for_dispatch") {
      return canApproveDispatch;
    }
    return isSalesUser;
  };

  const handleStatusUpdateClick = (shipment, order) => {
    const statusInfo = getStatusUpdateInfo(shipment);
    if (!statusInfo) return;
    if (!canPerformShipmentStatusAction(shipment, order, statusInfo)) return;

    setEditingShipmentForStatusUpdate({ ...shipment, order: order || {} });
    setStatusUpdateFile(null);
    setStatusUpdatePanelPackagesFile(null);
    setStatusUpdateHardwarePackagesFile(null);
    setStatusUpdateInvoiceFile(null);
    setStatusUpdateVehicleNumber("");
    setStatusUpdateDriverNumber("");
    setStatusUpdateOtp("");
    setStatusUpdatePaymentValue("");
    setStatusUpdatePaymentDate("");
    setStatusUpdateOtherReason(false);
    setStatusUpdateApproveReason("");
    setStatusUpdateModalOpen(true);
  };

  const handleSaveStatusUpdate = async () => {
    if (!editingShipmentForStatusUpdate) return;

    const statusInfo = getStatusUpdateInfo(editingShipmentForStatusUpdate);
    if (!statusInfo) return;
    const orderForShipment = editingShipmentForStatusUpdate.order;
    if (
      !canPerformShipmentStatusAction(
        editingShipmentForStatusUpdate,
        orderForShipment,
        statusInfo
      )
    )
      return;

    // Validate required fields based on status
    if (statusInfo.nextStatus === "ready_to_dispatch") {
      const hasPanels = getShipmentPartsCount(editingShipmentForStatusUpdate) > 0;
      const hasHardwares = getShipmentHardwaresCount(editingShipmentForStatusUpdate) > 0;
      const missingCSVs = [];

      if (hasPanels && !statusUpdatePanelPackagesFile && !statusUpdateFile) {
        missingCSVs.push("panel_packages_csv");
      }
      if (hasHardwares && !statusUpdateHardwarePackagesFile) {
        missingCSVs.push("hardware_packages_csv");
      }

      if (missingCSVs.length > 0) {
        setIsNotificationVisible(true);
        setNotificationMessage({
          type: "warning",
          text: `Required package CSVs are missing when marking shipment ready to dispatch: ${missingCSVs.join(", ")}`
        });
        return;
      }
    }

    if (statusInfo.nextStatus === "dispatch" && !statusUpdateInvoiceFile) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "warning",
        text: "Invoice file is required for dispatch status"
      });
      return;
    }

    if (statusInfo.nextStatus === "dispatch" && !statusUpdateOtp) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "warning",
        text: "OTP is required for dispatch status"
      });
      return;
    }

    try {
      setIsLoading(true);

      // Upload invoice first if dispatch status
      if (statusInfo.nextStatus === "dispatch" && statusUpdateInvoiceFile) {
        await uploadShipmentInvoice({
          orderId: editingShipmentForStatusUpdate.order.id,
          shipmentId: editingShipmentForStatusUpdate.id,
          invoiceFile: statusUpdateInvoiceFile,
        });
      }

      // Update shipment status
      // Use statusUpdatePanelPackagesFile, fallback to statusUpdateFile for backward compatibility
      const panelPackagesFile = statusUpdatePanelPackagesFile || statusUpdateFile;
      const response = await updateShipmentDate({
        orderId: editingShipmentForStatusUpdate.order.id,
        shipmentId: editingShipmentForStatusUpdate.id,
        status: statusInfo.nextStatus,
        panelPackagesFile: panelPackagesFile || null,
        hardwarePackagesFile: statusUpdateHardwarePackagesFile || null,
        otp: statusInfo.nextStatus === "dispatch" ? statusUpdateOtp : null,
        paymentValue: statusUpdatePaymentValue || null,
        paymentDate: statusUpdatePaymentDate || null,
        approveReason: statusUpdateApproveReason?.trim() || null,
      });

      // If approval_remarks comes in the response, save it to order.approval_remarks
      if (response?.approval_remarks && response?.order) {
        response.order.approval_remarks = response.approval_remarks;
      }

      fetchOrders();
      setStatusUpdateModalOpen(false);
      setEditingShipmentForStatusUpdate(null);
      setStatusUpdateFile(null);
      setStatusUpdatePanelPackagesFile(null);
      setStatusUpdateHardwarePackagesFile(null);
      setStatusUpdateInvoiceFile(null);
      setStatusUpdateVehicleNumber("");
      setStatusUpdateDriverNumber("");
      setStatusUpdateOtp("");
      setStatusUpdatePaymentValue("");
      setStatusUpdatePaymentDate("");
      setStatusUpdateOtherReason(false);
      setStatusUpdateApproveReason("");
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "success",
        text: `Shipment status updated to ${statusInfo.nextStatus}`
      });
    } catch (error) {
      setIsNotificationVisible(true);
      const errorMessage = error.message || error.error || error || "Unknown error";
      if (statusInfo.nextStatus === "dispatch") {
        setNotificationMessage({
          type: "danger",
          text: `Failed to update shipment status. ${errorMessage}. Invoice may have been uploaded but status was not updated.`
        });
      } else {
        setNotificationMessage({
          type: "danger",
          text: "Failed to update shipment status: " + errorMessage
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get shipment display date
  const getShipmentDisplayDate = (shipment) => {
    if (shipment.completed_date) {
      return formatDate(shipment.completed_date);
    }
    if (shipment.planned_date) {
      return formatDate(shipment.planned_date);
    }
    return "Not set";
  };

  const getStatusLabel = (status) => {
    const match = STATUS_OPTIONS.find((s) => s.value === status);
    return match ? match.label : status;
  };

  const getOrderStatusActionInfo = (order) => {
    if (!order) return null;

    const backendAction = Array.isArray(order.action_buttons)
      ? order.action_buttons.find((action) => action?.new_status && !action?.disabled)
      : null;

    if (backendAction) {
      return backendAction;
    }


    return null;
  };

  const handleOrderStatusAction = async (order) => {
    const actionInfo = getOrderStatusActionInfo(order);
    if (!actionInfo) return;

    const confirmMessage =
      actionInfo.message ||
      `Are you sure you want to change order status to "${getStatusLabel(actionInfo.new_status)}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsLoading(true);
      await updateOrderStatus({
        projectId: order.project_id,
        orderId: order.id,
        status: actionInfo.new_status,
        siteMapUrl: order.site_map_url || "",
      });
      fetchOrders();
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "success",
        text: `Order status updated to ${getStatusLabel(actionInfo.new_status)}`,
      });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotificationMessage({
        type: "danger",
        text:
          "Failed to update order status: " +
          (error.message || error.error || error || "Unknown error"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const statusOptions = [
    { label: "Approved", value: "approved" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Placed", value: "placed" },
    { label: "Production Planning", value: "production_planning" },
    { label: "Production", value: "production" },
    { label: "Partially Delivered", value: "partially_delivered" },
    { label: "Delivered", value: "delivered" },
  ];

  // Status options for Pending tab (without Delivered)
  const statusOptionsPending = statusOptions.filter(opt => opt.value !== "delivered");

  // Status options for Shipments tab (shipment statuses)
  const shipmentStatusOptions = [
    { label: "Draft", value: "draft" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Ready", value: "ready_to_dispatch" },
    { label: "Approved", value: "approve_for_dispatch" },
    { label: "Dispatched", value: "dispatch" },
  ];

  const generatePlacedOptions = () => {
    const options = [];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Last 6 months in reverse
    for (let i = 0; i < 6; i++) {
      const d = new Date(currentYear, currentMonth - i, 1);
      options.push({
        label: formatMonthYear(d),
        value: `m-${d.getFullYear()}-${d.getMonth() + 1}`,
      });
    }

    options.push({ label: "──────────", isDisabled: true });

    // Financial year quarters in reverse order
    const quarters = [
      { q: "Q4", months: [0, 1, 2] },
      { q: "Q3", months: [9, 10, 11] },
      { q: "Q2", months: [6, 7, 8] },
      { q: "Q1", months: [3, 4, 5] },
    ];

    const fy = now.getMonth() >= 3 ? now.getFullYear() + 1 : now.getFullYear();
    const allFYs = [fy - 1]; // FY 24–25

    allFYs.forEach((fyYear) => {
      quarters.forEach(({ q, months }) => {
        const baseYear = q === "Q4" ? fyYear - 1 : fyYear;
        const startMonth = Math.min(...months);
        const startDate = new Date(baseYear, startMonth, 1);
        if (startDate <= now) {
          options.push({
            label: `${q} ${String(fyYear - 1).slice(2)}-${String(fyYear).slice(
              2
            )}`,
            value: `q-${fyYear}-${q}`,
          });
        }
      });
    });

    options.push({ label: "──────────", isDisabled: true });

    options.push({
      label: `FY ${String(fy - 1).slice(2)}-${String(fy).slice(2)}`,
      value: `fy-${fy}`,
    });

    return options;
  };

  const isInPlacedFilter = (date) => {
    if (!placedFilter.length || !date) return true;
    const d = new Date(date);
    return placedFilter.some((filter) => {
      const [type, y, v] = filter.value.split("-");
      if (type === "m") {
        return d.getFullYear() === +y && d.getMonth() + 1 === +v;
      } else if (type === "q") {
        const fy = +y;
        const qMap = {
          Q1: [3, 4, 5],
          Q2: [6, 7, 8],
          Q3: [9, 10, 11],
          Q4: [0, 1, 2],
        };
        const months = qMap[v];
        const year = v === "Q4" ? fy - 1 : fy;
        return d.getFullYear() === year && months.includes(d.getMonth());
      } else if (type === "fy") {
        const fy = +y;
        const start = new Date(fy - 1, 3, 1);
        const end = new Date(fy, 2, 31);
        return d >= start && d <= end;
      }
      return true;
    });
  };

  const filteredOrders = orders.filter((o) => {
    const searchLower = searchTerm.toLowerCase();

    // Filter by project type
    let projectTypeMatch = true;
    if (projectTypeFilter === "main") {
      projectTypeMatch = !o.is_additional && !o.is_support;
    } else if (projectTypeFilter === "additional") {
      projectTypeMatch = o.is_additional;
    } else if (projectTypeFilter === "support") {
      projectTypeMatch = o.is_support;
    }


    // Filter by tab
    const isDelivered = o.status === "delivered";
    if (activeTab === "delivered" && !isDelivered) return false;
    if (activeTab === "pending" && isDelivered) return false;

    return (
      (!selectedStatus || o.status === selectedStatus) &&
      isInPlacedFilter(o.placed_date) &&
      projectTypeMatch &&
      (o.name.toLowerCase().includes(searchLower) ||
        o.identifier.toLowerCase().includes(searchLower) ||
        o.organization_name.toLowerCase().includes(searchLower) ||
        ("" + o.id).includes(searchLower) ||
        o.status.toLowerCase().includes(searchLower))
    );
  });

  const totalValue = filteredOrders.reduce((sum, o) => sum + (o.value || 0), 0);

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const aVal = a[sortKey] || "";
    const bVal = b[sortKey] || "";
    if (aVal < bVal) return sortAsc ? -1 : 1;
    if (aVal > bVal) return sortAsc ? 1 : -1;
    return 0;
  });

  const renderSortIcon = (key) => {
    if (key !== sortKey) return "";
    return sortAsc ? "▲" : "▼";
  };

  const renderShipmentSortIcon = (key) => {
    if (key !== shipmentSortKey) return "";
    return shipmentSortAsc ? "▲" : "▼";
  };

  // Flatten all shipments from all orders with order context
  const getAllShipments = () => {
    const allShipments = [];
    orders.forEach((order) => {
      const orderShipments = order.shipments || [];
      orderShipments.forEach((shipment) => {
        allShipments.push({
          ...shipment,
          order: order
        });
      });
    });
    return allShipments;
  };

  // Get shipment date for sorting
  const getShipmentDateForSort = (shipment) => {
    if (shipment.completed_date) {
      return new Date(shipment.completed_date).getTime();
    }
    if (shipment.planned_date) {
      return new Date(shipment.planned_date).getTime();
    }
    return 0;
  };

  // Sort shipments
  const getSortedShipments = (shipments) => {
    return [...shipments].sort((a, b) => {
      let aVal, bVal;

      if (shipmentSortKey === "date") {
        aVal = getShipmentDateForSort(a);
        bVal = getShipmentDateForSort(b);
      } else if (shipmentSortKey === "customer") {
        aVal = (a.order?.organization_name || "").toLowerCase();
        bVal = (b.order?.organization_name || "").toLowerCase();
      } else if (shipmentSortKey === "order") {
        aVal = (a.order?.name || "").toLowerCase();
        bVal = (b.order?.name || "").toLowerCase();
      } else if (shipmentSortKey === "value") {
        aVal = a.value || a.order?.value || 0;
        bVal = b.value || b.order?.value || 0;
      } else {
        aVal = "";
        bVal = "";
      }

      if (aVal < bVal) return shipmentSortAsc ? -1 : 1;
      if (aVal > bVal) return shipmentSortAsc ? 1 : -1;
      return 0;
    });
  };

  const handleShipmentSort = (key) => {
    if (key === shipmentSortKey) {
      setShipmentSortAsc(!shipmentSortAsc);
    } else {
      setShipmentSortKey(key);
      setShipmentSortAsc(true);
    }
  };

  // Reusable component for order info icon and modal
  const OrderInfoIcon = ({ order }) => {
    const hasInfo = order.contact_number || order.site_location || order.site_map_url;

    if (!hasInfo) {
      return null;
    }

    return (
      <span
        className="d-inline-block"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedOrderForInfo(order);
          setOrderInfoModalOpen(true);
        }}
        style={{ cursor: 'pointer' }}
      >
        <i
          className="fa fa-info-circle text-primary pointer"
          style={{ fontSize: "0.85rem" }}
        />
      </span>
    );
  };

  return (
    <>
      {notificationMessage.text && (
        <Notification
          color={notificationMessage.type}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notificationMessage.text}
        </Notification>
      )}
      <Row className="border p-3 bg-white rounded">
      <Nav className="mb-3 pb-2">
        <NavItem>
          <NavLink
            onClick={() => {
              setMainTab("orders");
              setSelectedStatus(""); // Reset status filter when switching tabs
            }}
            className={`px-3 py-2 fw-semibold me-3 ${
              mainTab === "orders"
                ? "text-primary border-bottom border-2 border-primary"
                : "text-dark"
            }`}
            style={{
              backgroundColor: "transparent",
              fontSize: "1rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Orders
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            onClick={() => {
              setMainTab("shipments");
              setSelectedStatus(""); // Reset status filter when switching tabs
            }}
            className={`px-3 py-2 fw-semibold me-3 ${
              mainTab === "shipments"
                ? "text-primary border-bottom border-2 border-primary"
                : "text-dark"
            }`}
            style={{
              backgroundColor: "transparent",
              fontSize: "1rem",
              border: "none",
              cursor: "pointer",
            }}
          >
            Shipments
          </NavLink>
        </NavItem>
      </Nav>
      {mainTab === "orders" && (
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={activeTab === "pending" ? "active" : ""}
              onClick={() => {
                setActiveTab("pending");
                setSelectedStatus(""); // Reset status filter when switching tabs
              }}
              style={{ cursor: "pointer" }}
            >
              Pending
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === "delivered" ? "active" : ""}
              onClick={() => {
                setActiveTab("delivered");
                setSelectedStatus(""); // Reset status filter when switching tabs
              }}
              style={{ cursor: "pointer" }}
            >
              Delivered
            </NavLink>
          </NavItem>
        </Nav>
      )}
      {mainTab === "orders" && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3 px-0">
            <div className="d-flex align-items-center d-flex gap-3">
              {activeTab === "pending" && (
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small me-2">Status:</span>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className={`btn btn-sm ${selectedStatus === "" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setSelectedStatus("")}
                      style={{ borderRadius: "20px", fontSize: "0.875rem" }}
                    >
                      All
                    </button>
                    {statusOptionsPending.map((option) => (
                      <button
                        key={option.value}
                        className={`btn btn-sm ${selectedStatus === option.value ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSelectedStatus(option.value)}
                        style={{ borderRadius: "20px", fontSize: "0.875rem" }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ minWidth: 200 }}>
                <Select
                  isMulti
                  options={generatePlacedOptions()}
                  value={placedFilter}
                  onChange={setPlacedFilter}
                  placeholder="Placed Date"
                />
              </div>
                <div style={{ minWidth: 200 }}>
            <Select
              options={[
                { label: "Main", value: "main" },
                { label: "Additional", value: "additional" },
                { label: "Support", value: "support" },
              ]}
              value={projectTypeFilter ? { label: projectTypeFilter === "main" ? "Main" : projectTypeFilter === "additional" ? "Additional" : "Support", value: projectTypeFilter } : null}
              onChange={(option) =>
                setProjectTypeFilter(option ? option.value : "")
              }
              isClearable
              placeholder="Project Type"
            />
          </div>
            </div>
            <input
              type="text"
              className="form-control w-25 me-3"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <Table
        bordered
        size="sm"
        className="align-middle mb-0 table-striped"
        style={{ fontSize: "0.9rem" }}
      >
        <thead className="bg-secondary text-white">
          <tr>
            <th
              scope="col"
              onClick={() => handleSort("organization_name")}
              className="bg-secondary text-white pointer"
            >
              Customer {renderSortIcon("organization_name")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("name")}
              className="bg-secondary text-white pointer"
            >
              Order {renderSortIcon("name")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("value")}
              className="bg-secondary text-white pointer"
            >
              Value {renderSortIcon("value")}
              <span className="ms-2 fw-normal">
                {formatCurrency(totalValue)}
              </span>
            </th>
            <th
              scope="col"
              onClick={() => handleSort("placed_date")}
              className="bg-secondary text-white pointer"
            >
              Placed On {renderSortIcon("placed_date")}
            </th>
            <th
              scope="col"
              onClick={() => handleSort("status")}
              className="bg-secondary text-white pointer"
            >
              Status {renderSortIcon("status")}
            </th>
            <th scope="col" className="bg-secondary text-white" style={{ minWidth: "650px" }}>
              Delivery
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedOrders.map((order) => (
            <tr key={order.id}>
              <td>{order.organization_name}</td>
              <td>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <a
                    href={`/projects/${order.project_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-dark text-decoration-none"
                  >
                    {order.name}
                  </a>
                  <span className="text-muted small">
                    #{order.identifier}
                  </span>
                  {order.additional_project && (
                    <>
                      <Badge
                        color="info"
                        className="text-nowrap"
                        id={`additional-tooltip-${order.id}`}
                      >
                        Additional
                      </Badge>
                      <UncontrolledTooltip
                        target={`additional-tooltip-${order.id}`}
                        placement="top"
                      >
                        <div style={{ textAlign: "left" }}>
                          Parent Project Order: {order.additional_project_order_identifier || "N/A"}
                        </div>
                      </UncontrolledTooltip>
                    </>
                  )}
                  {order.support_project && (
                    <>
                      <Badge
                        color="warning"
                        className="text-nowrap"
                        id={`support-tooltip-${order.id}`}
                      >
                        Support
                      </Badge>
                      <UncontrolledTooltip
                        target={`support-tooltip-${order.id}`}
                        placement="top"
                      >
                        <div style={{ textAlign: "left" }}>
                          Ticked Support Order: {order.support_project_order_identifier || "N/A"}
                        </div>
                      </UncontrolledTooltip>
                    </>
                  )}
                  <OrderInfoIcon order={order} />
                </div>
              </td>
              <td>{formatCurrency(order.value)}</td>
              <td>
                {order.placed_date ? (
                  formatDate(order.placed_date)
                ) : (
                  <span className="text-muted">Not Set</span>
                )}
              </td>
              <td>
                <div className="d-flex flex-column gap-1">
                  <span>{getStatusLabel(order.status)}</span>
                  {(() => {
                    const actionInfo = getOrderStatusActionInfo(order);
                    if (!actionInfo) return null;

                    return (
                      <Button
                        color="primary"
                        size="sm"
                        className="w-fit-content"
                        onClick={() => handleOrderStatusAction(order)}
                        disabled={isLoading}
                        style={{ fontSize: "0.7rem", padding: "0.15rem 0.45rem" }}
                      >
                        {actionInfo.text}
                      </Button>
                    );
                  })()}
                </div>
              </td>
              <td className="p-2" style={{ minWidth: "650px" }}>
                {(() => {
                  const allShipments = order.shipments || [];

                  // For Approved / Confirmed orders, hide detailed delivery and show only a dash
                  if (order.status === "approved" || order.status === "confirmed") {
                    return <span className="text-muted">-</span>;
                  }

                  return allShipments.length > 0 ? (
                    <Table size="sm" className="mb-0 w-auto" style={{ fontSize: "0.85rem", backgroundColor: "transparent" }}>
                      <tbody>
                        {sortShipmentsByNumber(allShipments).map((shipment, index) => {
                          const dateId = `tooltip-${shipment.id}`;
                          const otpTooltipId = `otp-tooltip-${shipment.id}`;
                          const originalPlannedDate = getOriginalPlannedDate(shipment);
                          const hasDateRevisions = shipment.date_revisions && Array.isArray(shipment.date_revisions) && shipment.date_revisions.length > 0;
                          return (
                            <tr key={shipment.id || index}>
                              <td className="p-1" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                <div className="d-flex flex-column">
                                  {(() => {
                                    const shipmentName = shipment.shipment_name || "";
                                    const displayName = shipmentName.replace(/^Shipment\s+/i, "");
                                    const hasDescription = (shipment.description || "").trim();
                                    return (
                                      <span className="fw-medium">
                                        {hasDescription
                                          ? `${displayName} - ${shipment.description}`
                                          : `${displayName} - Description`}
                                        {order.is_sales_user && (
                                          <i
                                            className="fa fa-pencil-alt pointer text-primary ms-1"
                                            style={{ fontSize: "0.75rem" }}
                                            title="Description"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEditDescriptionClick(shipment, order);
                                            }}
                                          />
                                        )}
                                      </span>
                                    );
                                  })()}
                                </div>
                                { shipment.extra && <Badge color="danger">extra</Badge>}
                              </td>
                              <td className="p-1 text-center" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                {getShipmentPartsCount(shipment) > 0 ? `${getShipmentPartsCount(shipment)} panel${getShipmentPartsCount(shipment) !== 1 ? 's' : ''}` : "–"}
                              </td>
                              <td className="p-1 text-center" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                {getShipmentHardwaresCount(shipment) > 0 ? `${getShipmentHardwaresCount(shipment)} hardware${getShipmentHardwaresCount(shipment) !== 1 ? 's' : ''}` : "–"}
                              </td>
                              <td className="p-1 text-center" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                {shipment.invoice_value ? formatCurrency(shipment.invoice_value) : "–"}
                              </td>
                              <td className="p-1" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "220px" }}>
                                <div className="d-flex align-items-center gap-2">
                                  <span id={shipment.otp ? otpTooltipId : dateId}>
                                    {getShipmentDisplayDate(shipment)}
                                  </span>
                                  {(() => {
                                    const revisionCount = getRevisionCount(shipment);
                                    const popoverId = `revision-popover-${shipment.id}`;
                                    if (revisionCount > 0) {
                                      const revisions = formatRevisions(shipment);
                                      return (
                                        <>
                                          <span
                                            id={popoverId}
                                            className="text-danger pointer fw-bold"
                                            style={{ fontSize: "0.85rem" }}
                                            onClick={() => togglePopover(shipment.id)}
                                          >
                                            ({revisionCount} {revisionCount === 1 ? 'revision' : 'revisions'})
                                          </span>
                                          <Popover
                                            target={popoverId}
                                            isOpen={popoverOpen[shipment.id] || false}
                                            toggle={() => togglePopover(shipment.id)}
                                            placement="right"
                                          >
                                            <PopoverHeader>
                                              <div className="d-flex justify-content-between align-items-center">
                                                <span>Revision History</span>
                                                <span
                                                  className="text-primary pointer"
                                                  onClick={() => togglePopover(shipment.id)}
                                                  style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                                >
                                                  Close
                                                </span>
                                              </div>
                                            </PopoverHeader>
                                            <PopoverBody>
                                              <div className="d-flex flex-column gap-2">
                                                {revisions.map((revision, idx) => (
                                                  <div key={idx} className="border-bottom pb-2">
                                                    <div className="fw-bold">Revision {revision.index}</div>
                                                    <div className="text-muted small">
                                                      New Date: {formatDate(revision.date, true)}
                                                    </div>
                                                    {revision.modifiedAt && (
                                                      <div className="text-muted small">
                                                        Updated on:  {formatDate(revision.modifiedAt, true)}
                                                      </div>
                                                    )}
                                                    {revision.reason && (
                                                      <div className="text-muted small">
                                                        Reason: {revision.reason}
                                                      </div>
                                                    )}
                                                    {revision.modifiedBy && (
                                                      <div className="text-muted small">
                                                        Modified by: {typeof revision.modifiedBy === 'object' ? revision.modifiedBy.name || revision.modifiedBy.id : revision.modifiedBy}
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </PopoverBody>
                                          </Popover>
                                        </>
                                      );
                                    }
                                    return null;
                                  })()}
                                  {!shipment.completed_date && order.is_sales_user && (
                                    <i
                                      className="fa fa-pencil text-dark pointer"
                                      title="Change Date"
                                      style={{ fontSize: "0.75rem" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingShipmentForDateChange({ ...shipment, order: order || {} });
                                        setRevisedDate("");
                                        setDateChangeReason("");
                                        setDateChangeModalOpen(true);
                                      }}
                                    ></i>
                                  )}
                                </div>
                              </td>
                              <td className="p-1 text-center" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                {(() => {
                                  const statusText = getShipmentStatusDisplay(shipment);
                                  const status = shipment.completed_date ? "completed" : shipment.status;

                                  let icon = null;
                                  if (status === "dispatch" || status === "completed") {
                                    icon = <i className="fa fa-check-circle text-success me-1" style={{ fontSize: "0.9rem" }}></i>;
                                  } else if (status === "ready_to_dispatch") {
                                    icon = <i className="fa fa-check-circle text-success me-1" style={{ fontSize: "0.9rem" }}></i>;
                                  } else if (status === "approve_for_dispatch") {
                                    icon = <i className="fa fa-thumbs-up text-success me-1" style={{ fontSize: "0.9rem" }}></i>;
                                  } else if (status === "confirmed") {
                                    icon = <i className="fa fa-clock text-warning me-1" style={{ fontSize: "0.9rem" }}></i>;
                                  }

                                  return (
                                    <span className="text-muted small d-flex align-items-center justify-content-center">
                                      {icon}
                                      {statusText}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="p-1" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                <div className="d-flex align-items-center gap-2">
                                  {(() => {
                                    const statusInfo = getStatusUpdateInfo(shipment);
                                    if (!statusInfo) return null;
                                    if (!canPerformShipmentStatusAction(shipment, order, statusInfo))
                                      return null;

                                    return (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusUpdateClick(shipment, order);
                                        }}
                                        disabled={isLoading}
                                        style={{ fontSize: "0.75rem", padding: "0.2rem 0.5rem" }}
                                        className="w-100"
                                      >
                                        {statusInfo.buttonLabel}
                                      </Button>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td className="p-1" style={{ border: "1px solid rgba(0, 0, 0, 0.1)", width: "100px" }}>
                                <div className="d-flex align-items-center gap-2">
                                  {!isDispatched(shipment) && (
                                    <>
                                      {order.is_sales_user && (
                                        <i
                                          className="fa fa-trash text-danger pointer"
                                          title="Delete Shipment"
                                          style={{ fontSize: "0.8rem" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteShipmentClick({ ...shipment, order });
                                          }}
                                        ></i>
                                      )}
                                      <span
                                        className="text-primary pointer"
                                        title="View Invoice Summary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewInvoiceSummary(order, shipment);
                                        }}
                                        style={{ fontSize: "0.9rem", fontWeight: "500" }}
                                      >
                                        ₹ Info
                                      </span>
                                    </>
                                  )}
                                  {(shipment.invoice_document || shipment.status === "dispatched" || shipment.status === "completed") && (
                                    <span
                                      className="text-primary pointer d-flex align-items-center gap-1"
                                      title="Download Invoice"
                                      style={{ fontSize: "0.9rem" }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadInvoice(order, shipment);
                                      }}
                                    >
                                      <i className="fa fa-download"></i>
                                      <span>Invoice</span>
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                  ) : (
                    <span className="text-muted">–</span>
                  );
                })()}
                {!["draft", "finalised", "approved", "confirmed", "delivered"].includes(order.status) &&(
                  <div className="text-muted d-flex align-items-center gap-2 mt-2">
                    {order.is_sales_user &&
                      (() => {
                        const allShipments = order.shipments || [];
                        return allShipments.length > 0 &&
                          order.status !== "approved" &&
                          order.status !== "confirmed" ? (
                          <span className="d-flex align-items-center gap-1">
                            <i
                              className="fa fa-plus-circle text-success pointer"
                              title="Add Shipment"
                              onClick={() => handleAddShipmentClick(order)}
                            ></i>
                            <span
                              className="text-success pointer"
                              onClick={() => handleAddShipmentClick(order)}
                              style={{ fontSize: "0.9rem" }}
                            >
                              Add
                            </span>
                          </span>
                        ) : null;
                      })()}
                    <span
                      className="text-primary pointer d-flex align-items-center gap-1"
                      onClick={() => {
                        navigate(`/manage_parts`, {
                          state: {
                            orderId: order.id,
                            projectId: order.project_id,
                            customerName: order.organization_name,
                            projectName: order.name,
                            orderIdentifier: order.identifier,
                            is_sales_user: order.is_sales_user,
                          },
                        });
                      }}
                      style={{ fontSize: "0.9rem", cursor: "pointer" }}
                    >
                      <i className="fa fa-cog"></i>
                      Manage
                    </span>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
        </>
      )}
      {mainTab === "shipments" && (
        <>
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={shipmentTab === "pending" ? "active" : ""}
                onClick={() => {
                  setShipmentTab("pending");
                  setSelectedStatus(""); // Reset status filter when switching tabs
                }}
                style={{ cursor: "pointer" }}
              >
                Pending
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={shipmentTab === "delivered" ? "active" : ""}
                onClick={() => {
                  setShipmentTab("delivered");
                  setSelectedStatus(""); // Reset status filter when switching tabs
                }}
                style={{ cursor: "pointer" }}
              >
                Delivered
              </NavLink>
            </NavItem>
          </Nav>
          <div className="d-flex justify-content-between align-items-center mb-3 px-0">
            <div className="d-flex align-items-center d-flex gap-3">
              {shipmentTab === "pending" && (
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted small me-2">Status:</span>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className={`btn btn-sm ${selectedStatus === "" ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setSelectedStatus("")}
                      style={{ borderRadius: "20px", fontSize: "0.875rem" }}
                    >
                      All
                    </button>
                    {shipmentStatusOptions.map((option) => (
                      <button
                        key={option.value}
                        className={`btn btn-sm ${selectedStatus === option.value ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setSelectedStatus(option.value)}
                        style={{ borderRadius: "20px", fontSize: "0.875rem" }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <input
              type="text"
              className="form-control w-25 me-3"
              placeholder="Search shipments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Table
            bordered
            size="sm"
            className="align-middle mb-0 table-striped"
            style={{ fontSize: "0.9rem" }}
          >
            <thead className="bg-secondary text-white">
              <tr>
                <th
                  scope="col"
                  onClick={() => handleShipmentSort("customer")}
                  className="bg-secondary text-white pointer"
                >
                  Customer {renderShipmentSortIcon("customer")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleShipmentSort("order")}
                  className="bg-secondary text-white pointer"
                >
                  Order {renderShipmentSortIcon("order")}
                </th>
                <th scope="col" className="bg-secondary text-white" style={{ width: "100px" }}>
                  Shipment
                </th>
                <th scope="col" className="bg-secondary text-white text-center" style={{ width: "80px" }}>
                  Panels
                </th>
                <th scope="col" className="bg-secondary text-white text-center" style={{ width: "80px" }}>
                  Hardwares
                </th>
                <th
                  scope="col"
                  onClick={() => handleShipmentSort("value")}
                  className="bg-secondary text-white pointer"
                >
                  Value {renderShipmentSortIcon("value")}
                </th>
                <th
                  scope="col"
                  onClick={() => handleShipmentSort("date")}
                  className="bg-secondary text-white pointer"
                  style={{ width: "220px" }}
                >
                  Date {renderShipmentSortIcon("date")}
                </th>
                <th scope="col" className="bg-secondary text-white text-center" style={{ width: "100px" }}>
                  Status
                </th>
                <th scope="col" className="bg-secondary text-white" style={{ width: "100px" }}>
                  Status Update
                </th>
                <th scope="col" className="bg-secondary text-white" style={{ width: "100px" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const allShipments = getAllShipments();
                const filteredShipments = allShipments.filter((shipment) => {
                  const searchLower = searchTerm.toLowerCase();
                  const order = shipment.order || {};

                  // Filter by tab (pending vs delivered)
                  const isDelivered = shipment.completed_date || shipment.status === "completed";
                  if (shipmentTab === "delivered" && !isDelivered) return false;
                  if (shipmentTab === "pending" && isDelivered) return false;

                  // Filter by shipment status (for pending tab)
                  if (shipmentTab === "pending" && selectedStatus) {
                    const shipmentStatus = shipment.completed_date ? "completed" : shipment.status;
                    if (shipmentStatus !== selectedStatus) {
                      return false;
                    }
                  }

                  return (
                    shipment.shipment_name?.toLowerCase().includes(searchLower) ||
                    order.name?.toLowerCase().includes(searchLower) ||
                    order.organization_name?.toLowerCase().includes(searchLower) ||
                    order.identifier?.toLowerCase().includes(searchLower) ||
                    ("" + order.id).includes(searchLower)
                  );
                });
                const sortedShipments = getSortedShipments(filteredShipments);
                return sortedShipments.map((shipment, index) => {
                  const order = shipment.order || {};
                  const dateId = `tooltip-${shipment.id}`;
                  const otpTooltipId = `otp-tooltip-${shipment.id}`;
                  const popoverId = `revision-popover-${shipment.id}`;
                  const revisionCount = getRevisionCount(shipment);
                  const revisions = revisionCount > 0 ? formatRevisions(shipment) : [];
                  const shipmentValue = shipment.invoice_value || 0;
                  return (
                    <tr key={shipment.id || index}>
                      <td>{order.organization_name || "–"}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <a
                            href={`/projects/${order.project_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-dark text-decoration-none"
                          >
                            {order.name || "–"}
                          </a>
                          {order.identifier && (
                            <span className="text-muted small">
                              #{order.identifier}
                            </span>
                          )}
                          <OrderInfoIcon order={order} />
                        </div>
                      </td>
                      <td className="p-1">
                        <div className="d-flex flex-column">
                          {(() => {
                            const shipmentName = shipment.shipment_name || "";
                            const displayName = shipmentName.replace(/^Shipment\s+/i, "");
                            const hasDescription = (shipment.description || "").trim();
                            return (
                              <span className="fw-medium">
                                {hasDescription
                                  ? `${displayName} - ${shipment.description}`
                                  : displayName}
                              </span>
                            );
                          })()}
                        </div>
                        {shipment.extra && <Badge color="danger" className="ms-1">extra</Badge>}
                      </td>
                      <td className="p-1 text-center">
                        {getShipmentPartsCount(shipment) > 0 ? getShipmentPartsCount(shipment) : "–"}
                      </td>
                      <td className="p-1 text-center">
                        {getShipmentHardwaresCount(shipment) > 0 ? getShipmentHardwaresCount(shipment) : "–"}
                      </td>
                      <td>{formatCurrency(shipmentValue)}</td>
                      <td className="p-1">
                        <div className="d-flex align-items-center gap-2">
                          <span id={shipment.otp ? otpTooltipId : dateId}>
                            {getShipmentDisplayDate(shipment)}
                          </span>
                          {revisionCount > 0 && (
                            <>
                              <span
                                id={popoverId}
                                className="text-danger pointer fw-bold"
                                style={{ fontSize: "0.85rem" }}
                                onClick={() => togglePopover(shipment.id)}
                              >
                                ({revisionCount} {revisionCount === 1 ? 'revision' : 'revisions'})
                              </span>
                              <Popover
                                target={popoverId}
                                isOpen={popoverOpen[shipment.id] || false}
                                toggle={() => togglePopover(shipment.id)}
                                placement="right"
                              >
                                <PopoverHeader>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span>Revision History</span>
                                    <span
                                      className="text-primary pointer"
                                      onClick={() => togglePopover(shipment.id)}
                                      style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                                    >
                                      Close
                                    </span>
                                  </div>
                                </PopoverHeader>
                                <PopoverBody>
                                  <div className="d-flex flex-column gap-2">
                                    {revisions.map((revision, idx) => (
                                      <div key={idx} className="border-bottom pb-2">
                                        <div className="fw-bold">Revision {revision.index}</div>
                                        <div className="text-muted small">
                                          New Date: {formatDate(revision.date, true)}
                                        </div>
                                        {revision.modifiedAt && (
                                          <div className="text-muted small">
                                            Updated on: {formatDate(revision.modifiedAt, true)}
                                          </div>
                                        )}
                                        {revision.reason && (
                                          <div className="text-muted small">
                                            Reason: {revision.reason}
                                          </div>
                                        )}
                                        {revision.modifiedBy && (
                                          <div className="text-muted small">
                                            Modified by: {typeof revision.modifiedBy === 'object' ? revision.modifiedBy.name || revision.modifiedBy.id : revision.modifiedBy}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </PopoverBody>
                              </Popover>
                            </>
                          )}
                          {!shipment.completed_date && order.is_sales_user && (
                            <i
                              className="fa fa-pencil text-dark pointer"
                              title="Change Date"
                              style={{ fontSize: "0.75rem" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingShipmentForDateChange({ ...shipment, order: order });
                                setRevisedDate("");
                                setDateChangeReason("");
                                setDateChangeModalOpen(true);
                              }}
                            ></i>
                          )}
                        </div>
                      </td>
                      <td className="p-1 text-center">
                        {(() => {
                          const statusText = getShipmentStatusDisplay(shipment);
                          const status = shipment.completed_date ? "completed" : shipment.status;

                          let icon = null;
                          if (status === "dispatch" || status === "completed") {
                            icon = <i className="fa fa-check-circle text-success me-1" style={{ fontSize: "0.9rem" }}></i>;
                          } else if (status === "ready_to_dispatch") {
                            icon = <i className="fa fa-check-circle text-success me-1" style={{ fontSize: "0.9rem" }}></i>;
                          } else if (status === "approve_for_dispatch") {
                            icon = <i className="fa fa-thumbs-up text-success me-1" style={{ fontSize: "0.9rem" }}></i>;
                          } else if (status === "confirmed") {
                            icon = <i className="fa fa-clock text-warning me-1" style={{ fontSize: "0.9rem" }}></i>;
                          }

                          return (
                            <span className="text-muted small d-flex align-items-center justify-content-center">
                              {icon}
                              {statusText}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="p-1">
                        <div className="d-flex align-items-center gap-2">
                          {(() => {
                            const statusInfo = getStatusUpdateInfo(shipment);
                            if (!statusInfo) return null;
                            if (!canPerformShipmentStatusAction(shipment, order, statusInfo))
                              return null;

                            return (
                              <Button
                                color="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusUpdateClick(shipment, order);
                                }}
                                disabled={isLoading}
                                style={{ fontSize: "0.75rem", padding: "0.2rem 0.5rem" }}
                                className="w-100"
                              >
                                {statusInfo.buttonLabel}
                              </Button>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="p-1">
                        <div className="d-flex align-items-center gap-2">
                          {!isDispatched(shipment) && (
                            <>
                              {order.is_sales_user && (
                                <i
                                  className="fa fa-trash text-danger pointer"
                                  title="Delete Shipment"
                                  style={{ fontSize: "0.8rem" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteShipmentClick({ ...shipment, order });
                                  }}
                                ></i>
                              )}
                              <span
                                className="text-primary pointer"
                                title="View Invoice Summary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewInvoiceSummary(order, shipment);
                                }}
                                style={{ fontSize: "0.9rem", fontWeight: "500" }}
                              >
                                ₹ Info
                              </span>
                            </>
                          )}
                          {(shipment.invoice_document || shipment.status === "dispatch" || shipment.status === "completed") && (
                            <span
                              className="text-primary pointer d-flex align-items-center gap-1"
                              title="Download Invoice"
                              style={{ fontSize: "0.9rem" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadInvoice(order, shipment);
                              }}
                            >
                              <i className="fa fa-download"></i>
                              <span>Invoice</span>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </Table>
        </>
      )}
      <Modal
        isOpen={addShipmentModalOpen}
        toggle={() => setAddShipmentModalOpen(false)}
      >
        <ModalHeader toggle={() => setAddShipmentModalOpen(false)}>
          Add New Shipment
        </ModalHeader>
        <ModalBody>
          {currentOrderForShipment && (
            <div className="mb-3">
              <label className="form-label">
                Planned Delivery Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control w-50"
                value={newShipmentPlannedDate}
                onChange={(e) => setNewShipmentPlannedDate(e.target.value)}
                required
              />
              <small className="text-muted">
                Select the planned delivery date for this shipment
              </small>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleAddShipment}
            disabled={!newShipmentPlannedDate || isLoading}
          >
            {isLoading ? "Adding..." : "Add Shipment"}
          </Button>
          <Button
            color="secondary"
            onClick={() => setAddShipmentModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={invoiceSummaryModalOpen}
        toggle={() => {
          setInvoiceSummaryModalOpen(false);
          setInvoiceSummaryData(null);
        }}
        style={{ maxWidth: "98vw", width: "98vw", margin: "1vh auto" }}
      >
        <ModalHeader toggle={() => {
          setInvoiceSummaryModalOpen(false);
          setInvoiceSummaryData(null);
        }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span className="fw-bold">Invoice Summary</span>
            <div>
              {copied ? (
                <span className="text-success small" style={{ fontSize: "0.85rem" }}>
                  <i className="fa fa-check me-1"></i>Copied
                </span>
              ) : (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={copyTableData}
                  disabled={!invoiceSummaryData || isLoadingInvoiceSummary || invoiceSummaryData?.length === 0}
                  style={{
                    fontSize: "0.85rem",
                    padding: "0.25rem 0.5rem"
                  }}
                >
                  <i className="fa fa-copy me-1"></i>Copy
                </Button>
              )}
            </div>
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "75vh", overflowY: "auto", padding: "1rem" }}>
          {isLoadingInvoiceSummary ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : invoiceSummaryData && invoiceSummaryData.length > 0 ? (
            <Table bordered hover responsive>
              <thead className="bg-light">
                <tr>
                  <th style={{ whiteSpace: "nowrap", fontSize: "0.9rem", width: "60px" }}>
                    S.No
                  </th>
                  {Object.keys(invoiceSummaryData[0]).map((header) => (
                    <th key={header} style={{ whiteSpace: "nowrap", fontSize: "0.9rem" }}>
                      {header.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoiceSummaryData.map((row, index) => (
                  <tr key={index}>
                    <td style={{ fontSize: "0.9rem", textAlign: "center", fontWeight: "500" }}>
                      {index + 1}
                    </td>
                    {Object.keys(invoiceSummaryData[0]).map((header) => {
                      const value = row[header];
                      let displayValue = "";

                      if (value !== null && value !== undefined) {
                        if (typeof value === 'number') {
                          if (header.includes('sqft') || header.includes('sq_ft')) {
                            displayValue = value.toFixed(3);
                          } else {
                            displayValue = value.toString();
                          }
                        } else {
                          displayValue = String(value);
                        }
                      }

                      return (
                        <td key={header} style={{ fontSize: "0.9rem" }}>
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5 text-muted">
              No data available
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setInvoiceSummaryModalOpen(false);
              setInvoiceSummaryData(null);
            }}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={dateChangeModalOpen}
        toggle={() => {
          setDateChangeModalOpen(false);
          setEditingShipmentForDateChange(null);
          setRevisedDate("");
          setDateChangeReason("");
        }}
      >
        <ModalHeader toggle={() => {
          setDateChangeModalOpen(false);
          setEditingShipmentForDateChange(null);
          setRevisedDate("");
          setDateChangeReason("");
        }}>
          Change Shipment Date
        </ModalHeader>
        <ModalBody>
          {editingShipmentForDateChange && (
            <>
              <div className="mb-3">
                <label className="form-label fw-bold">Current Date</label>
                <div className="form-control-plaintext">
                  {getShipmentDisplayDate(editingShipmentForDateChange)}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Revised Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={revisedDate}
                  onChange={(e) => setRevisedDate(e.target.value)}
                  style={{ width: "auto", maxWidth: "200px" }}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Reason <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={dateChangeReason}
                  onChange={(e) => setDateChangeReason(e.target.value)}
                  placeholder="Please provide a reason for changing the shipment date"
                />
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setDateChangeModalOpen(false);
              setEditingShipmentForDateChange(null);
              setRevisedDate("");
              setDateChangeReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveDateChange}
            disabled={isLoading || !revisedDate || !dateChangeReason || dateChangeReason.trim() === ""}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={statusUpdateModalOpen}
        toggle={() => {
          setStatusUpdateModalOpen(false);
          setEditingShipmentForStatusUpdate(null);
          setStatusUpdateFile(null);
          setStatusUpdatePanelPackagesFile(null);
          setStatusUpdateHardwarePackagesFile(null);
          setStatusUpdateInvoiceFile(null);
          setStatusUpdateVehicleNumber("");
          setStatusUpdateDriverNumber("");
          setStatusUpdateOtp("");
          setStatusUpdatePaymentValue("");
          setStatusUpdatePaymentDate("");
          setStatusUpdateOtherReason(false);
          setStatusUpdateApproveReason("");
        }}
      >
        <ModalHeader toggle={() => {
          setStatusUpdateModalOpen(false);
          setEditingShipmentForStatusUpdate(null);
          setStatusUpdateFile(null);
          setStatusUpdatePanelPackagesFile(null);
          setStatusUpdateHardwarePackagesFile(null);
          setStatusUpdateInvoiceFile(null);
          setStatusUpdateVehicleNumber("");
          setStatusUpdateDriverNumber("");
          setStatusUpdateOtp("");
          setStatusUpdatePaymentValue("");
          setStatusUpdatePaymentDate("");
          setStatusUpdateOtherReason(false);
          setStatusUpdateApproveReason("");
        }}>
          {editingShipmentForStatusUpdate && getStatusUpdateInfo(editingShipmentForStatusUpdate)?.buttonLabel
            ? `Update Status - ${getStatusUpdateInfo(editingShipmentForStatusUpdate).buttonLabel}`
            : "Update Shipment Status"}
        </ModalHeader>
        <ModalBody>
          {editingShipmentForStatusUpdate && (() => {
            const statusInfo = getStatusUpdateInfo(editingShipmentForStatusUpdate);
            if (!statusInfo) return null;

            const hasFormFields = statusInfo.nextStatus === "ready_to_dispatch" || statusInfo.nextStatus === "dispatch";
            const hasPanels = getShipmentPartsCount(editingShipmentForStatusUpdate) > 0;
            const hasHardwares = getShipmentHardwaresCount(editingShipmentForStatusUpdate) > 0;

            return (
              <>
                {!hasFormFields && (
                  <div className="mb-3">
                    <p className="mb-0">
                      Are you sure you want to update the shipment status to <strong>{statusInfo.buttonLabel}</strong>?
                    </p>
                  </div>
                )}

                {statusInfo.nextStatus === "ready_to_dispatch" && (
                  <>
                    {hasPanels && (
                      <div className="mb-3">
                        <label className="form-label">
                          Panel Packages CSV <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control w-50"
                          accept=".csv"
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0];
                            setStatusUpdatePanelPackagesFile(selectedFile || null);
                          }}
                        />
                        <small className="text-muted">
                          CSV format: Sub Assembly ID, Box Index
                        </small>
                        {!statusUpdatePanelPackagesFile && !statusUpdateFile && (
                          <div className="text-danger small mt-1">
                            Panel packages CSV is required when shipment has panels
                          </div>
                        )}
                      </div>
                    )}
                    {hasHardwares && (
                      <div className="mb-3">
                        <label className="form-label">
                          Hardware Packages CSV <span className="text-danger">*</span>
                        </label>
                        <input
                          type="file"
                          className="form-control w-50"
                          accept=".csv"
                          onChange={(e) => {
                            const selectedFile = e.target.files?.[0];
                            setStatusUpdateHardwarePackagesFile(selectedFile || null);
                          }}
                        />
                        <small className="text-muted">
                          CSV format: sku, package_number
                        </small>
                        {!statusUpdateHardwarePackagesFile && (
                          <div className="text-danger small mt-1">
                            Hardware packages CSV is required when shipment has hardwares
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {statusInfo.nextStatus === "dispatch" && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">
                        Invoice <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control w-50"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setStatusUpdateInvoiceFile(e.target.files?.[0] || null)}
                      />
                      <small className="text-muted">
                        Upload invoice document (PDF, JPG, PNG)
                      </small>
                      {!statusUpdateInvoiceFile && (
                        <div className="text-danger small mt-1">
                          Invoice is required for dispatch status
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        OTP <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control w-50"
                        placeholder="Enter OTP"
                        value={statusUpdateOtp}
                        onChange={(e) => setStatusUpdateOtp(e.target.value)}
                      />
                      <small className="text-muted">
                        Enter the OTP for dispatch status
                      </small>
                      {!statusUpdateOtp && (
                        <div className="text-danger small mt-1">
                          OTP is required for dispatch status
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Vehicle Number</label>
                      <input
                        type="text"
                        className="form-control w-50"
                        placeholder="Enter vehicle number"
                        value={statusUpdateVehicleNumber}
                        onChange={(e) => setStatusUpdateVehicleNumber(e.target.value)}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Driver Number</label>
                      <input
                        type="text"
                        className="form-control w-50"
                        placeholder="Enter driver phone number"
                        value={statusUpdateDriverNumber}
                        onChange={(e) => setStatusUpdateDriverNumber(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Payment fields - shown only when approving (approve_for_dispatch) */}
                {statusInfo.nextStatus === "approve_for_dispatch" && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">
                        Payment Value (₹)
                      </label>
                      <input
                        type="number"
                        className="form-control w-50"
                        placeholder="Enter payment amount"
                        value={statusUpdatePaymentValue}
                        onChange={(e) => setStatusUpdatePaymentValue(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Payment Date
                      </label>
                      <input
                        type="date"
                        className="form-control w-50"
                        value={statusUpdatePaymentDate}
                        onChange={(e) => setStatusUpdatePaymentDate(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>
                    <div className="mb-3">
                      <div className="d-flex column-gap-2 align-items-center">
                        <input
                          type="checkbox"
                          id="status-update-other-reason"
                          checked={statusUpdateOtherReason}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setStatusUpdateOtherReason(isChecked);
                            // Reset approve reason when checkbox is unchecked
                            if (!isChecked) {
                              setStatusUpdateApproveReason("");
                            }
                          }}
                          disabled={isLoading}
                          className="m-0"
                        />
                        <label htmlFor="status-update-other-reason" className="m-0 form-label">
                          Other Reason
                        </label>
                      </div>
                    </div>
                    {statusUpdateOtherReason && (
                      <div className="mb-3">
                        <label className="form-label">
                          Approve Reason <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control w-50 ${statusUpdateOtherReason && !statusUpdateApproveReason?.trim() ? "border-danger" : ""}`}
                          placeholder="Enter approve reason"
                          value={statusUpdateApproveReason}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 250) {
                              setStatusUpdateApproveReason(value);
                            }
                          }}
                          disabled={isLoading}
                          maxLength={250}
                        />
                        {statusUpdateOtherReason && !statusUpdateApproveReason?.trim() && (
                          <small className="text-danger">Approve reason is required</small>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setStatusUpdateModalOpen(false);
              setEditingShipmentForStatusUpdate(null);
              setStatusUpdateFile(null);
              setStatusUpdatePanelPackagesFile(null);
              setStatusUpdateHardwarePackagesFile(null);
              setStatusUpdateInvoiceFile(null);
              setStatusUpdateVehicleNumber("");
              setStatusUpdateDriverNumber("");
              setStatusUpdateOtp("");
              setStatusUpdatePaymentValue("");
              setStatusUpdatePaymentDate("");
              setStatusUpdateOtherReason(false);
              setStatusUpdateApproveReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveStatusUpdate}
            disabled={
              isLoading ||
              (editingShipmentForStatusUpdate &&
                (() => {
                  const statusInfo = getStatusUpdateInfo(editingShipmentForStatusUpdate);
                  if (!statusInfo) return true;
                  
                  if (statusInfo.nextStatus === "approve_for_dispatch") {
                    const hasPayment = statusUpdatePaymentValue && statusUpdatePaymentDate;
                    const hasApproveReason = statusUpdateOtherReason && statusUpdateApproveReason?.trim();
                    const isFormValid = hasPayment || hasApproveReason;
                    
                    if (statusUpdateOtherReason && !statusUpdateApproveReason?.trim()) return true;
                    
                    if (!isFormValid) return true;
                  }
                  
                  if (statusInfo.nextStatus === "ready_to_dispatch") {
                    const hasPanels = getShipmentPartsCount(editingShipmentForStatusUpdate) > 0;
                    const hasHardwares = getShipmentHardwaresCount(editingShipmentForStatusUpdate) > 0;
                    if (hasPanels && !statusUpdatePanelPackagesFile && !statusUpdateFile) return true;
                    if (hasHardwares && !statusUpdateHardwarePackagesFile) return true;
                  }
                  if (statusInfo.nextStatus === "dispatch" && (!statusUpdateInvoiceFile || !statusUpdateOtp)) return true;
                  return false;
                })())
            }
          >
            {isLoading ? "Saving..." : "Update Status"}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={deleteShipmentModalOpen}
        toggle={() => {
          setDeleteShipmentModalOpen(false);
          setShipmentToDelete(null);
          setReplacementShipmentId("");
        }}
      >
        <ModalHeader toggle={() => {
          setDeleteShipmentModalOpen(false);
          setShipmentToDelete(null);
          setReplacementShipmentId("");
        }}>
          Delete Shipment
        </ModalHeader>
        <ModalBody>
          {shipmentToDelete && (
            <>
              <div className="mb-3">
                <p>Are you sure you want to delete <strong>{shipmentToDelete.shipment_name}</strong>?</p>
                {getShipmentPartsCount(shipmentToDelete) > 0 && (
                  <p>
                    This shipment has {getShipmentPartsCount(shipmentToDelete)} panel{getShipmentPartsCount(shipmentToDelete) !== 1 ? 's' : ''}
                    {getShipmentHardwaresCount(shipmentToDelete) > 0 && ` & ${getShipmentHardwaresCount(shipmentToDelete)} hardware${getShipmentHardwaresCount(shipmentToDelete) !== 1 ? 's' : ''}`}. Please select an alternative shipment to move them to.
                  </p>
                )}
              </div>
              {getShipmentPartsCount(shipmentToDelete) > 0 && (
                <div className="mb-3">
                  <label className="form-label">
                    Move panels to shipment <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={getAlternativeShipments(shipmentToDelete.order, shipmentToDelete.id).map(s => {
                      const panelsText = getShipmentPartsCount(s) > 0 ? `${getShipmentPartsCount(s)} panel${getShipmentPartsCount(s) !== 1 ? 's' : ''}` : "0 panels";
                      const hardwaresText = getShipmentHardwaresCount(s) > 0 ? `${getShipmentHardwaresCount(s)} hardware${getShipmentHardwaresCount(s) !== 1 ? 's' : ''}` : "0 hardwares";
                      return {
                        label: `${s.shipment_name} (${panelsText} | ${hardwaresText})`,
                        value: s.id
                      };
                    })}
                    value={replacementShipmentId ? {
                      label: getAlternativeShipments(shipmentToDelete.order, shipmentToDelete.id)
                        .find(s => s.id === replacementShipmentId)?.shipment_name || "",
                      value: replacementShipmentId
                    } : null}
                    onChange={(option) => setReplacementShipmentId(option ? option.value : "")}
                    placeholder="Select shipment..."
                    isClearable={false}
                  />
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setDeleteShipmentModalOpen(false);
              setShipmentToDelete(null);
              setReplacementShipmentId("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleDeleteShipment}
            disabled={
              isLoading ||
              (getShipmentPartsCount(shipmentToDelete) > 0 && !replacementShipmentId)
            }
          >
            {isLoading ? "Deleting..." : "Delete Shipment"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={editDescriptionModalOpen}
        toggle={() => {
          setEditDescriptionModalOpen(false);
          setEditingShipmentForDescription(null);
          setEditDescriptionValue("");
        }}
      >
        <ModalHeader
          toggle={() => {
            setEditDescriptionModalOpen(false);
            setEditingShipmentForDescription(null);
            setEditDescriptionValue("");
          }}
        >
          Update Description
        </ModalHeader>
        <ModalBody>
          {editingShipmentForDescription && (
            <>
              <p className="text-muted small mb-2">
                {editingShipmentForDescription.shipment.shipment_name}
              </p>
              <label className="form-label">Description</label>
              <Input
                type="text"
                maxLength={20}
                value={editDescriptionValue}
                onChange={(e) => setEditDescriptionValue((e.target.value || "").slice(0, 20))}
                placeholder="Enter description (max 20 chars)"
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setEditDescriptionModalOpen(false);
              setEditingShipmentForDescription(null);
              setEditDescriptionValue("");
            }}
          >
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveDescription} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Order Info Modal */}
      <Modal isOpen={orderInfoModalOpen} toggle={() => setOrderInfoModalOpen(false)} centered>
        <ModalHeader toggle={() => setOrderInfoModalOpen(false)}>
          Site Details
        </ModalHeader>
        <ModalBody>
          {selectedOrderForInfo && (
            <>
              {selectedOrderForInfo.contact_number && (
                <div className="mb-3">
                  <strong>Contact:</strong> {selectedOrderForInfo.contact_number}
                </div>
              )}
              {selectedOrderForInfo.site_location && (
                <div className="mb-3">
                  <strong>Location:</strong> {selectedOrderForInfo.site_location}
                </div>
              )}
              {selectedOrderForInfo.site_map_url && (
                <div>
                  <strong>Map URL:</strong>{" "}
                  <a
                    href={selectedOrderForInfo.site_map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      wordBreak: "break-all"
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(selectedOrderForInfo.site_map_url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {selectedOrderForInfo.site_map_url}
                  </a>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setOrderInfoModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </Row>
    </>
  );
};

export default Orders;
